import { useEffect, useState } from "react";
import ThemeDevPanel from "./ThemeDevPanel.tsx";
import { isLocalhost } from "../util/env.ts";

/* Gate for the dev theme playground. On localhost it's always available. In
 * production it stays completely hidden until unlocked with a secret key
 * sequence (the Konami code), after which the unlock is remembered per-browser
 * in localStorage. The panel only mutates local CSS variables + localStorage -
 * nothing server-side - so an unlock is harmless if discovered. */

const FLAG = "slpmeup:theme-lab-unlocked";

// ↑ ↑ ↓ ↓ ← → ← →
const UNLOCK_CODE = [
  "arrowup",
  "arrowup",
  "arrowdown",
  "arrowdown",
  "arrowleft",
  "arrowright",
  "arrowleft",
  "arrowright",
];

function initiallyEnabled(): boolean {
  if (isLocalhost()) return true;
  try {
    return localStorage.getItem(FLAG) === "1";
  } catch {
    return false;
  }
}

export default function ThemeLab() {
  const [enabled, setEnabled] = useState(initiallyEnabled);

  // While locked, watch for the unlock sequence. Removed once enabled.
  useEffect(() => {
    if (enabled) return;
    let buffer: string[] = [];
    function onKey(e: KeyboardEvent) {
      const key = e.key.length === 1 ? e.key.toLowerCase() : e.key.toLowerCase();
      buffer = [...buffer, key].slice(-UNLOCK_CODE.length);
      if (buffer.length === UNLOCK_CODE.length && UNLOCK_CODE.every((k, i) => k === buffer[i])) {
        try {
          localStorage.setItem(FLAG, "1");
        } catch {
          /* ignore */
        }
        setEnabled(true);
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [enabled]);

  if (!enabled) return null;

  // Locking is only meaningful in production; on localhost the panel is always on.
  const onLock = isLocalhost()
    ? undefined
    : () => {
        try {
          localStorage.removeItem(FLAG);
        } catch {
          /* ignore */
        }
        setEnabled(false);
      };

  return <ThemeDevPanel onLock={onLock} />;
}
